/** Bidirectional contract for renderer operations served by the native FRAME bridge. */

type NativeDesktopArgumentValue =
  | string
  | number
  | boolean
  | null
  | readonly string[]
  | Readonly<Record<string, unknown>>
  | undefined;

export type NativeDesktopArguments = Readonly<Record<string, unknown>>;
export type NativeDesktopRequestFrame = Record<string, unknown> & {
  type: WorkspaceFrameOperation['requestType'];
  request_id?: string;
};
export type NativeDesktopResponseFrame = Record<string, unknown> & {
  type: WorkspaceFrameOperation['responseType'];
  request_id: string;
};

type ResponseKind =
  | 'ack'
  | 'string'
  | 'boolean'
  | 'number'
  | 'record'
  | 'nullable-record'
  | 'array';
type ResponseSpec = { field?: string; kind: ResponseKind };
type OperationSpec = {
  requestType: string;
  responseType: string;
  /** renderer camelCase key -> FRAME snake_case key */
  fields: Readonly<Record<string, string>>;
  nullDefaults?: readonly string[];
  response: ResponseSpec;
};

const ack = <const ResponseType extends string>(responseType: ResponseType) => ({
  responseType,
  response: { kind: 'ack' as const },
});
const value = <
  const ResponseType extends string,
  const Field extends string,
  const Kind extends Exclude<ResponseKind, 'ack'>,
>(
  responseType: ResponseType,
  field: Field,
  kind: Kind,
) => ({
  responseType,
  response: { field, kind },
});

/**
 * Single source of truth for operations that can bypass Electron IPC and use
 * the local CLI server. Keeping this in the protocol package makes additions
 * visible to both ends of the bridge.
 */
export const nativeDesktopFrameOperations = {
  fs_read_text_file: {
    requestType: 'workspace_read_text_file',
    fields: { workspaceRoot: 'workspace_root', path: 'path', line: 'line', limit: 'limit' },
    nullDefaults: ['line', 'limit'],
    ...value('workspace_text_file', 'content', 'string'),
  },
  fs_read_binary_file: {
    requestType: 'workspace_read_binary_file',
    fields: { workspaceRoot: 'workspace_root', path: 'path' },
    ...value('workspace_binary_file', 'content', 'string'),
  },
  fs_write_text_file: {
    requestType: 'workspace_write_text_file',
    fields: { workspaceRoot: 'workspace_root', path: 'path', content: 'content' },
    ...ack('workspace_file_written'),
  },
  fs_remove_file: {
    requestType: 'workspace_remove_file',
    fields: { workspaceRoot: 'workspace_root', path: 'path' },
    ...ack('workspace_file_removed'),
  },
  workspace_list_directory: {
    requestType: 'workspace_list_directory',
    fields: { workspaceRoot: 'workspace_root', relativePath: 'relative_path' },
    ...value('workspace_directory', 'entries', 'array'),
  },
  workspace_git_review: {
    requestType: 'workspace_git_review',
    fields: { workspaceRoot: 'workspace_root' },
    ...value('workspace_git_review', 'review', 'record'),
  },
  workspace_checkpoint_diff: {
    requestType: 'workspace_checkpoint_diff',
    fields: {
      workspaceRoot: 'workspace_root',
      before: 'before',
      after: 'after',
      path: 'path',
      exclude: 'exclude',
    },
    nullDefaults: ['path'],
    ...value('workspace_checkpoint_diff', 'patch', 'string'),
  },
  workspace_checkpoint_revert: {
    requestType: 'workspace_checkpoint_revert',
    fields: {
      workspaceRoot: 'workspace_root',
      turns: 'turns',
      overwrite: 'overwrite',
      keep: 'keep',
      dryRun: 'dry_run',
    },
    ...value('workspace_checkpoint_revert', 'result', 'record'),
  },
  workspace_git_stage: {
    requestType: 'workspace_git_stage',
    fields: { workspaceRoot: 'workspace_root', path: 'path' },
    ...ack('workspace_git_action_completed'),
  },
  workspace_git_unstage: {
    requestType: 'workspace_git_unstage',
    fields: { workspaceRoot: 'workspace_root', path: 'path' },
    ...ack('workspace_git_action_completed'),
  },
  workspace_git_revert: {
    requestType: 'workspace_git_revert',
    fields: { workspaceRoot: 'workspace_root', path: 'path' },
    ...ack('workspace_git_action_completed'),
  },
  workspace_git_status: {
    requestType: 'workspace_git_status',
    fields: { workspaceRoot: 'workspace_root' },
    ...value('workspace_git_status', 'status', 'record'),
  },
  workspace_git_generation_context: {
    requestType: 'workspace_git_generation_context',
    fields: { workspaceRoot: 'workspace_root', paths: 'paths' },
    ...value('workspace_git_generation_context', 'context', 'record'),
  },
  workspace_git_commit: {
    requestType: 'workspace_git_commit',
    fields: { workspaceRoot: 'workspace_root', message: 'message', push: 'push', paths: 'paths' },
    ...value('workspace_git_commit', 'result', 'record'),
  },
  workspace_git_push: {
    requestType: 'workspace_git_push',
    fields: { workspaceRoot: 'workspace_root' },
    ...value('workspace_git_push', 'result', 'record'),
  },
  workspace_git_snapshot: {
    requestType: 'workspace_git_snapshot',
    fields: { workspaceRoot: 'workspace_root', baseRef: 'base_ref' },
    ...value('workspace_git_snapshot', 'snapshot', 'record'),
  },
  workspace_git_switch_branch: {
    requestType: 'workspace_git_switch_branch',
    fields: {
      workspaceRoot: 'workspace_root',
      branch: 'branch',
      stash: 'stash',
      remote: 'remote',
      diverged: 'diverged',
    },
    ...value('workspace_git_switch_branch', 'result', 'record'),
  },
  workspace_git_fetch: {
    requestType: 'workspace_git_fetch',
    fields: { workspaceRoot: 'workspace_root' },
    ...ack('workspace_git_fetch'),
  },
  workspace_git_create_branch: {
    requestType: 'workspace_git_create_branch',
    fields: { workspaceRoot: 'workspace_root', branch: 'branch' },
    ...ack('workspace_git_create_branch'),
  },
  workspace_git_clone: {
    requestType: 'workspace_git_clone',
    fields: { destinationRoot: 'destination_root', cloneUrl: 'clone_url' },
    ...value('workspace_git_clone', 'path', 'string'),
  },
  workspace_pull_request_cockpit: {
    requestType: 'workspace_pull_request_cockpit',
    fields: { workspaceRoot: 'workspace_root' },
    ...value('workspace_pull_request_cockpit', 'cockpit', 'record'),
  },
  workspace_git_create_pull_request: {
    requestType: 'workspace_git_create_pull_request',
    fields: { workspaceRoot: 'workspace_root', title: 'title', body: 'body', draft: 'draft' },
    ...value('workspace_git_create_pull_request', 'result', 'record'),
  },
  workspace_git_merge_pull_request: {
    requestType: 'workspace_git_merge_pull_request',
    fields: {
      workspaceRoot: 'workspace_root',
      method: 'method',
      expectedHeadSha: 'expected_head_sha',
    },
    ...ack('workspace_git_merge_pull_request'),
  },
  workspace_git_mutate_pull_request: {
    requestType: 'workspace_git_mutate_pull_request',
    fields: {
      workspaceRoot: 'workspace_root',
      action: 'action',
      expectedHeadSha: 'expected_head_sha',
      title: 'title',
      body: 'body',
      decision: 'decision',
      reviewers: 'reviewers',
      method: 'method',
      threadId: 'thread_id',
      commentId: 'comment_id',
      reaction: 'reaction',
    },
    ...ack('workspace_git_mutate_pull_request'),
  },
  workspace_environment_get: {
    requestType: 'workspace_environment_get',
    fields: { workspaceRoot: 'workspace_root' },
    ...value('workspace_environment_get', 'environment', 'record'),
  },
  workspace_environment_set: {
    requestType: 'workspace_environment_set',
    fields: { workspaceRoot: 'workspace_root', environment: 'environment' },
    ...ack('workspace_environment_set'),
  },
  workspace_git_create_managed_worktree: {
    requestType: 'workspace_git_create_managed_worktree',
    fields: {
      workspaceRoot: 'workspace_root',
      ownerThreadId: 'owner_thread_id',
      startingBranch: 'starting_branch',
      startingRemote: 'starting_remote',
      environment: 'environment',
      worktreeRoot: 'worktree_root',
      worktreePath: 'worktree_path',
    },
    ...value('workspace_git_create_managed_worktree', 'worktree', 'record'),
  },
  workspace_git_capture_worktree: {
    requestType: 'workspace_git_capture_worktree',
    fields: {
      workspaceRoot: 'workspace_root',
      ownerThreadId: 'owner_thread_id',
      message: 'message',
      worktreeRoot: 'worktree_root',
    },
    ...value('workspace_git_capture_worktree', 'capture', 'record'),
  },
  workspace_git_integrate: {
    requestType: 'workspace_git_integrate',
    fields: {
      workspaceRoot: 'workspace_root',
      name: 'name',
      commits: 'commits',
      base: 'base',
      worktreeRoot: 'worktree_root',
    },
    ...value('workspace_git_integrate', 'integration', 'record'),
  },
  workspace_git_handoff_worktree: {
    requestType: 'workspace_git_handoff_worktree',
    fields: { worktreePath: 'worktree_path', destination: 'destination' },
    ...value('workspace_git_handoff_worktree', 'worktree', 'record'),
  },
  workspace_git_restore_worktree: {
    requestType: 'workspace_git_restore_worktree',
    fields: { worktreePath: 'worktree_path' },
    ...value('workspace_git_restore_worktree', 'worktree', 'nullable-record'),
  },
  workspace_git_delete_worktree: {
    requestType: 'workspace_git_delete_worktree',
    fields: { workspaceRoot: 'workspace_root', worktreePath: 'worktree_path' },
    ...ack('workspace_git_delete_worktree'),
  },
  workspace_git_cleanup_worktrees: {
    requestType: 'workspace_git_cleanup_worktrees',
    fields: {
      workspaceRoot: 'workspace_root',
      protectedOwnerIds: 'protected_owner_ids',
      keepCount: 'keep_count',
      worktreeRoot: 'worktree_root',
    },
    ...value('workspace_git_cleanup_worktrees', 'removed', 'array'),
  },
  workspace_run_command: {
    requestType: 'workspace_run_command',
    fields: { workspaceRoot: 'workspace_root', relativePath: 'relative_path', command: 'command' },
    ...value('workspace_run_command', 'output', 'record'),
  },
  acp_agent_catalog: {
    requestType: 'acp_agent_catalog',
    fields: {},
    ...value('acp_agent_catalog', 'agents', 'array'),
  },
  acp_discover_agents: {
    requestType: 'acp_discover_agents',
    fields: {},
    ...value('acp_discover_agents', 'agents', 'array'),
  },
  agent_terminal_create: {
    requestType: 'agent_terminal_create',
    fields: {
      terminalId: 'terminal_id',
      workspaceRoot: 'workspace_root',
      command: 'command',
      args: 'args',
      env: 'env',
      cwd: 'cwd',
      outputByteLimit: 'output_byte_limit',
    },
    ...ack('agent_terminal_create'),
  },
  agent_terminal_output: {
    requestType: 'agent_terminal_output',
    fields: { terminalId: 'terminal_id' },
    ...value('agent_terminal_output', '$frame', 'record'),
  },
  agent_terminal_wait: {
    requestType: 'agent_terminal_wait',
    fields: { terminalId: 'terminal_id' },
    ...value('agent_terminal_wait', 'exit_status', 'nullable-record'),
  },
  agent_terminal_kill: {
    requestType: 'agent_terminal_kill',
    fields: { terminalId: 'terminal_id' },
    ...ack('agent_terminal_kill'),
  },
  agent_terminal_release: {
    requestType: 'agent_terminal_release',
    fields: { terminalId: 'terminal_id' },
    ...ack('agent_terminal_release'),
  },
  agent_terminal_read_background_output: {
    requestType: 'agent_terminal_read_background_output',
    fields: { path: 'path' },
    ...value('agent_terminal_read_background_output', 'content', 'string'),
  },
  agent_terminal_kill_background: {
    requestType: 'agent_terminal_kill_background',
    fields: { path: 'path' },
    ...ack('agent_terminal_kill_background'),
  },
} as const satisfies Record<string, OperationSpec>;

export type NativeDesktopFrameCommand = keyof typeof nativeDesktopFrameOperations;
export type WorkspaceFrameOperation =
  (typeof nativeDesktopFrameOperations)[NativeDesktopFrameCommand];
export type NativeDesktopOperationArguments<Command extends NativeDesktopFrameCommand> = Partial<
  Record<keyof (typeof nativeDesktopFrameOperations)[Command]['fields'], NativeDesktopArgumentValue>
>;

export function isNativeDesktopFrameCommand(command: string): command is NativeDesktopFrameCommand {
  return Object.hasOwn(nativeDesktopFrameOperations, command);
}

/** Maps renderer camelCase arguments to a typed protocol request frame. */
export function encodeNativeDesktopRequest(
  command: NativeDesktopFrameCommand,
  args: NativeDesktopArguments,
): NativeDesktopRequestFrame {
  const operation = nativeDesktopFrameOperations[command];
  const frame: Record<string, unknown> = { type: operation.requestType };
  for (const [rendererKey, wireKey] of Object.entries(operation.fields)) {
    // Preserve explicit nulls and undefined values required by the wire schema.
    const argument = args[rendererKey];
    frame[wireKey] =
      argument === undefined &&
      'nullDefaults' in operation &&
      (operation.nullDefaults as readonly string[]).includes(rendererKey)
        ? null
        : argument;
  }
  return frame as NativeDesktopRequestFrame;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function matchesResponse(kind: ResponseKind, payload: unknown): boolean {
  if (kind === 'ack') return true;
  if (kind === 'nullable-record') return payload === null || isRecord(payload);
  if (kind === 'record') return isRecord(payload);
  if (kind === 'array') return Array.isArray(payload);
  return typeof payload === kind && (kind !== 'number' || Number.isFinite(payload));
}

/** Checks the discriminant and payload before exposing an untrusted server response. */
export function decodeNativeDesktopResponse(
  command: NativeDesktopFrameCommand,
  frame: Record<string, unknown>,
): unknown {
  const operation = nativeDesktopFrameOperations[command];
  if (frame['type'] !== operation.responseType) {
    throw new Error(`Unexpected "${String(frame['type'])}" response for "${command}".`);
  }
  if (operation.response.kind === 'ack') return undefined;
  const field = operation.response.field;
  if (field === undefined) throw new Error(`Missing response field contract for "${command}".`);
  const payload = field === '$frame' ? frame : frame[field];
  if (!matchesResponse(operation.response.kind, payload)) {
    throw new Error(
      `Invalid "${operation.responseType}" response: expected ${operation.response.kind} payload in "${field}".`,
    );
  }
  if (command === 'agent_terminal_output') {
    if (
      typeof frame['output'] !== 'string' ||
      typeof frame['truncated'] !== 'boolean' ||
      !matchesResponse('nullable-record', frame['exit_status'])
    ) {
      throw new Error('Invalid "agent_terminal_output" response payload.');
    }
    return {
      output: frame['output'],
      truncated: frame['truncated'],
      exitStatus: frame['exit_status'],
    };
  }
  return payload;
}
